import { apiClient, ApiResponse } from './apiClient';

export interface PaymentMethod {
  id: string;
  provider_name: string;
  provider_code: string;
  image_url: string;
  description: string;
  status: 'active' | 'blocked';
  display_order: number;
  public_key: string;
  secret_key: string;
  merchant_id: string;
  callback_url: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentMethodFilters {
  search?: string;
  status?: 'active' | 'blocked';
  page?: number;
  limit?: number;
}

export interface PaymentMethodListResponse {
  payment_methods: PaymentMethod[];
  total: number;
  total_pages: number;
  current_page: number;
}

export interface CreatePaymentMethodRequest {
  provider_name: string;
  provider_code: string;
  image_url: string;
  description: string;
  status: 'active' | 'blocked';
  display_order: number;
  public_key: string;
  secret_key: string;
  merchant_id: string;
  callback_url: string;
}

export interface UpdatePaymentMethodRequest {
  provider_name?: string;
  provider_code?: string;
  image_url?: string;
  description?: string;
  status?: 'active' | 'blocked';
  display_order?: number;
  public_key?: string;
  secret_key?: string;
  merchant_id?: string;
  callback_url?: string;
}

export interface PaymentMethodStats {
  total_methods: number;
  active_methods: number;
  inactive_methods: number;
}

export class PaymentMethodsService {
  private baseUrl = '/admin/payment-methods';

  // Get all payment methods with filters
  async getPaymentMethods(filters: PaymentMethodFilters = {}): Promise<ApiResponse<PaymentMethodListResponse>> {
    try {
      console.log('🔧 PaymentMethodsService.getPaymentMethods called:', { filters });
      
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const queryString = params.toString();
      const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
      
      console.log('🔧 Making GET request to:', url);
      console.log('🔧 Query parameters:', Object.fromEntries(params));
      
      const response = await apiClient.get<PaymentMethodListResponse>(url);
      
      console.log('🔧 PaymentMethodsService.getPaymentMethods response:', response);
      console.log('🔧 Response success:', response.success);
      console.log('🔧 Response data type:', typeof response.data);
      console.log('🔧 Response data:', response.data);
      
      if (response.success && response.data) {
        // Handle the specific response format you provided
        let paymentMethods: PaymentMethod[] = [];
        let total = 0;
        let totalPages = 1;
        let currentPage = 1;
        
        console.log('🔧 Processing response data:', response.data);
        
        if ((response.data as any).payment_methods) {
          // Direct response format as specified
          console.log('🔧 Using direct response format');
          const data = response.data as any;
          paymentMethods = data.payment_methods || [];
          total = data.total || paymentMethods.length;
          totalPages = data.total_pages || 1;
          currentPage = data.current_page || 1;
        } else if (Array.isArray(response.data)) {
          // Fallback: direct array response
          console.log('🔧 Using direct array format');
          paymentMethods = response.data;
          total = paymentMethods.length;
        } else {
          // Try to extract from nested data structure
          console.log('🔧 Trying nested data extraction');
          const nestedData = (response.data as any).data;
          if (nestedData && nestedData.payment_methods) {
            paymentMethods = nestedData.payment_methods || [];
            total = nestedData.total || paymentMethods.length;
            totalPages = nestedData.total_pages || 1;
            currentPage = nestedData.current_page || 1;
          }
        }
        
        console.log('🔧 Final extracted payment methods:', paymentMethods);
        console.log('🔧 Final payment methods count:', paymentMethods.length);
        
        const listResponse: PaymentMethodListResponse = {
          payment_methods: paymentMethods,
          total,
          total_pages: totalPages,
          current_page: currentPage
        };
        
        return {
          success: true,
          data: listResponse,
          message: 'Payment methods retrieved successfully'
        };
      }
      
      return {
        success: false,
        data: {
          payment_methods: [],
          total: 0,
          total_pages: 1,
          current_page: 1
        },
        message: 'No payment methods data received'
      };
    } catch (error: any) {
      console.error('🔧 PaymentMethodsService.getPaymentMethods error:', error);
      console.error('🔧 Error status:', error.response?.status);
      console.error('🔧 Error data:', error.response?.data);
      
      // Handle specific error cases
      let errorMessage = 'Error fetching payment methods';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error - Payment methods endpoint may not be implemented yet';
        console.warn('🔧 500 Error: Payment methods endpoint appears to be not implemented on the backend');
      } else if (error.response?.status === 404) {
        errorMessage = 'Payment methods endpoint not found';
        console.warn('🔧 404 Error: Payment methods endpoint not found');
      } else if (error.response?.status === 401) {
        errorMessage = 'Unauthorized access to payment methods';
      } else if (error.response?.status === 403) {
        errorMessage = 'Forbidden access to payment methods';
      } else if (error.code === 'ECONNREFUSED' || error.code === 'NETWORK_ERROR') {
        errorMessage = 'Cannot connect to server';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: {
          payment_methods: [],
          total: 0,
          total_pages: 1,
          current_page: 1
        },
        message: errorMessage
      };
    }
  }

  // Get single payment method by ID
  async getPaymentMethod(id: string): Promise<ApiResponse<PaymentMethod>> {
    try {
      console.log('🔧 PaymentMethodsService.getPaymentMethod called:', { id });
      
      const response = await apiClient.get<PaymentMethod>(`${this.baseUrl}/${id}`);
      
      console.log('🔧 PaymentMethodsService.getPaymentMethod response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 PaymentMethodsService.getPaymentMethod error:', error);
      throw error;
    }
  }

  // Create new payment method
  async createPaymentMethod(data: CreatePaymentMethodRequest): Promise<ApiResponse<PaymentMethod>> {
    try {
      console.log('🔧 PaymentMethodsService.createPaymentMethod called:', { data });
      
      // Prepare data for API
      const methodData = {
        provider_name: data.provider_name.trim(),
        provider_code: data.provider_code.trim().toUpperCase(),
        image_url: data.image_url.trim(),
        description: data.description.trim(),
        status: data.status,
        display_order: Number(data.display_order),
        public_key: data.public_key.trim(),
        secret_key: data.secret_key.trim(),
        merchant_id: data.merchant_id.trim(),
        callback_url: data.callback_url.trim()
      };
      
      console.log('🔧 PaymentMethodsService.createPaymentMethod prepared data:', methodData);
      console.log('🔧 Making POST request to:', this.baseUrl);
      console.log('🔧 Request payload:', JSON.stringify(methodData, null, 2));
      
      const response = await apiClient.post<PaymentMethod>(this.baseUrl, methodData);
      
      console.log('🔧 PaymentMethodsService.createPaymentMethod response:', response);
      console.log('🔧 Response status:', response.success);
      console.log('🔧 Response data:', response.data);
      
      return response;
    } catch (error: any) {
      console.error('🔧 PaymentMethodsService.createPaymentMethod error:', error);
      console.error('🔧 Error response data:', error.response?.data);
      console.error('🔧 Error response status:', error.response?.status);
      
      let errorMessage = 'Error creating payment method';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error - Payment methods creation endpoint may not be implemented yet';
        console.warn('🔧 500 Error: Payment methods creation endpoint appears to be not implemented on the backend');
      } else if (error.response?.status === 404) {
        errorMessage = 'Payment methods creation endpoint not found';
        console.warn('🔧 404 Error: Payment methods creation endpoint not found');
      } else if (error.response?.status === 422) {
        errorMessage = 'Validation error - Please check your input data';
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          // Handle validation errors array
          const validationErrors = error.response.data.errors.map((err: any) => {
            if (typeof err === 'string') return err;
            if (err.msg && err.path) return `${err.path}: ${err.msg}`;
            return err.msg || err.message || 'Validation error';
          });
          errorMessage = validationErrors.join(', ');
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.status === 401) {
        errorMessage = 'Unauthorized access to create payment methods';
      } else if (error.response?.status === 403) {
        errorMessage = 'Forbidden access to create payment methods';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage,
        errors: error.response?.data?.errors || []
      };
    }
  }

  // Update existing payment method
  async updatePaymentMethod(id: string, data: UpdatePaymentMethodRequest): Promise<ApiResponse<PaymentMethod>> {
    try {
      console.log('🔧 PaymentMethodsService.updatePaymentMethod called:', { id, data });
      
      // Prepare data for API - send all fields that can be updated
      const updateData: any = {};
      
      if (data.provider_name !== undefined && data.provider_name !== null && data.provider_name.trim() !== '') {
        updateData.provider_name = data.provider_name.trim();
        console.log('🔧 Adding provider_name:', updateData.provider_name);
      }
      if (data.provider_code !== undefined && data.provider_code !== null && data.provider_code.trim() !== '') {
        updateData.provider_code = data.provider_code.trim().toUpperCase();
        console.log('🔧 Adding provider_code:', updateData.provider_code);
      }
      if (data.image_url !== undefined && data.image_url !== null && data.image_url.trim() !== '') {
        updateData.image_url = data.image_url.trim();
        console.log('🔧 Adding image_url:', updateData.image_url);
      }
      if (data.description !== undefined && data.description !== null && data.description.trim() !== '') {
        updateData.description = data.description.trim();
        console.log('🔧 Adding description:', updateData.description);
      }
      if (data.status !== undefined && data.status !== null) {
        updateData.status = data.status;
        console.log('🔧 Adding status:', updateData.status);
      }
      if (data.display_order !== undefined && data.display_order !== null) {
        updateData.display_order = Number(data.display_order);
        console.log('🔧 Adding display_order:', updateData.display_order);
      }
      if (data.public_key !== undefined && data.public_key !== null && data.public_key.trim() !== '') {
        updateData.public_key = data.public_key.trim();
        console.log('🔧 Adding public_key:', updateData.public_key.substring(0, 20) + '...');
      }
      if (data.secret_key !== undefined && data.secret_key !== null && data.secret_key.trim() !== '') {
        updateData.secret_key = data.secret_key.trim();
        console.log('🔧 Adding secret_key:', '***HIDDEN***');
      }
      if (data.merchant_id !== undefined && data.merchant_id !== null && data.merchant_id.trim() !== '') {
        updateData.merchant_id = data.merchant_id.trim();
        console.log('🔧 Adding merchant_id:', updateData.merchant_id);
      }
      if (data.callback_url !== undefined && data.callback_url !== null && data.callback_url.trim() !== '') {
        updateData.callback_url = data.callback_url.trim();
        console.log('🔧 Adding callback_url:', updateData.callback_url);
      }
      
      console.log('🔧 PaymentMethodsService.updatePaymentMethod prepared data (PUT):', updateData);
      console.log('🔧 Making PUT request to:', `${this.baseUrl}/${id}`);
      console.log('🔧 Request payload keys:', Object.keys(updateData));
      
      // Check if we have any data to send
      if (Object.keys(updateData).length === 0) {
        console.warn('🔧 WARNING: No data to update! updateData is empty');
        return {
          success: false,
          data: null as any,
          message: 'No data provided for update'
        };
      }
      
      const response = await apiClient.put<PaymentMethod>(`${this.baseUrl}/${id}`, updateData);
      
      console.log('🔧 PaymentMethodsService.updatePaymentMethod response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 PaymentMethodsService.updatePaymentMethod error:', error);
      
      let errorMessage = 'Error updating payment method';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const validationErrors = error.response.data.errors.map((err: any) => {
          if (typeof err === 'string') return err;
          if (err.msg && err.path) return `${err.path}: ${err.msg}`;
          return err.msg || err.message || 'Validation error';
        });
        errorMessage = validationErrors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage,
        errors: error.response?.data?.errors || []
      };
    }
  }

  // Delete payment method
  async deletePaymentMethod(id: string): Promise<ApiResponse<void>> {
    try {
      console.log('🔧 PaymentMethodsService.deletePaymentMethod called:', { id });
      
      const response = await apiClient.delete<void>(`${this.baseUrl}/${id}`);
      
      console.log('🔧 PaymentMethodsService.deletePaymentMethod response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 PaymentMethodsService.deletePaymentMethod error:', error);
      throw error;
    }
  }

  // Toggle payment method status
  async toggleStatus(id: string): Promise<ApiResponse<PaymentMethod>> {
    try {
      console.log('🔧 PaymentMethodsService.toggleStatus called:', { id });
      console.log('🔧 Making PATCH request to:', `${this.baseUrl}/${id}/toggle-status`);
      
      const response = await apiClient.patch<PaymentMethod>(`${this.baseUrl}/${id}/toggle-status`);
      
      console.log('🔧 PaymentMethodsService.toggleStatus response:', response);
      console.log('🔧 Response success:', response.success);
      console.log('🔧 Response data:', response.data);
      
      return response;
    } catch (error: any) {
      console.error('🔧 PaymentMethodsService.toggleStatus error:', error);
      console.error('🔧 Error status:', error.response?.status);
      console.error('🔧 Error data:', error.response?.data);
      
      let errorMessage = 'Error toggling payment method status';
      
      if (error.response?.status === 500) {
        errorMessage = 'Server error - Toggle status endpoint may not be implemented yet';
        console.warn('🔧 500 Error: Toggle status endpoint appears to be not implemented on the backend');
      } else if (error.response?.status === 404) {
        errorMessage = 'Payment method not found or toggle endpoint not available';
        console.warn('🔧 404 Error: Payment method not found or endpoint not implemented');
      } else if (error.response?.status === 401) {
        errorMessage = 'Unauthorized access to toggle payment method status';
      } else if (error.response?.status === 403) {
        errorMessage = 'Forbidden access to toggle payment method status';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage
      };
    }
  }

  // Get payment method statistics (calculated from data)
  calculateStats(paymentMethods: PaymentMethod[]): PaymentMethodStats {
    return {
      total_methods: paymentMethods.length,
      active_methods: paymentMethods.filter(method => method.status === 'active').length,
      inactive_methods: paymentMethods.filter(method => method.status === 'blocked').length,
    };
  }
}

export const paymentMethodsService = new PaymentMethodsService();
